'use client';

import React, { useEffect, useState } from 'react';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import { twJoin } from 'tailwind-merge';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import Button from '@/ui/components/shared/Button';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { DocShapeId } from '@/utils/shapeIds';
import { Bundle } from '@/ui/services/BundleService';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import { urlParameterCodes } from '@/constants/urlParams';
import { BundleProduct } from '@/ui/services/BundleProductService';
import { NestedProductVariantsFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { useTranslations } from 'next-intl';
import { buildTrackingId } from '@/utils/tracking-helper';
import { ShsCardProps } from './types/ShsCardProps';

const createButtonLink = (
  bundle: Bundle | null,
  smartlockFromBundle: BundleProduct | undefined,
  productVariant: NestedProductVariantsFragment | undefined,
): string | undefined => {
  if (bundle?.hasSubscription && smartlockFromBundle) {
    return `${smartlockFromBundle.path}?${urlParameterCodes.productDetail.chooserOpen}=true&${urlParameterCodes.productDetail.bundleId}=${bundle.documentExtRef}&${urlParameterCodes.productDetail.subscription}=true`;
  }

  if (productVariant?.subscriptionPlans) {
    return `${productVariant.product?.path}?${urlParameterCodes.productDetail.serviceId}=true&${urlParameterCodes.productDetail.sku}=${productVariant.sku}`;
  }

  if (productVariant?.product?.path) {
    return productVariant?.product?.path;
  }

  return undefined;
};

export default function ShsCard({ card, index, trackingPrefix }: Readonly<ShsCardProps>) {
  const t = useTranslations('ShsPricing');
  const [isHidden, setIsHidden] = useState(false);
  const { areaVariables } = useLocalization();
  const { formatCurrency } = useCurrencyFormatter();
  const isMiddleCard = index === 1;
  const optionalTitle = extractComponentFromChunk(card, 'title', 'SingleLineContent')?.text;
  const product = extractComponentFromChunk(card, 'product', 'ItemRelationsContent');
  const productVariant = product?.productVariants?.[0];

  const subscriptionPeriod = productVariant?.subscriptionPlans?.[0].periods?.[0];

  const subscriptionInitialPrice = subscriptionPeriod?.initial?.price;
  const subscriptionRecurringPrice = subscriptionPeriod?.recurring?.price;

  const subscriptionHasFreeTrial = subscriptionInitialPrice === 0;

  const currency = areaVariables?.priceVariant?.currency;
  const productItem = product?.items?.[0];
  const isBundle = productItem?.shape.identifier === DocShapeId.bundle;
  const bundle = isBundle ? new Bundle(productItem, areaVariables?.priceVariantId) : null;

  const featuresHeading = extractComponentFromChunk(card, 'features-heading', 'RichTextContent');
  const features = extractComponentFromChunk(card, 'features', 'RichTextContent');
  const shortDescription = extractComponentFromChunk(card, 'short-description', 'RichTextContent');
  const buttonText = extractComponentFromChunk(card, 'button-text', 'SingleLineContent')?.text;
  const optionalButtonLink = extractComponentFromChunk(
    card,
    'optional-button-link',
    'ItemRelationsContent',
  )?.items?.[0].path;

  const smartlockFromBundle = bundle?.products.find(
    (bundleProduct) => bundleProduct.shape === DocShapeId.smartLock,
  );

  const buttonLink = createButtonLink(bundle, smartlockFromBundle, productVariant);

  const ctaButtonTrackingId = buildTrackingId(trackingPrefix, optionalTitle || `card-${index}`);

  useEffect(() => {
    if (window.innerWidth < 768 && !isMiddleCard) {
      setIsHidden(true);
    }
  }, [isMiddleCard]);

  return (
    <div
      className={twJoin(
        'relative rounded-3xl border bg-white px-6 pt-10',
        isMiddleCard ? 'border-primary-100 pb-8 drop-shadow-xl' : 'border-neutral-10',
        isBundle && 'mt-10 rounded-t-none md:mt-0',
        !isHidden && 'pb-8',
      )}
    >
      {bundle?.totalDiscount && (
        <div className="absolute -left-px -right-px bottom-full rounded-t-3xl bg-primary-100 py-2 text-center text-lg font-bold">
          {t('Save as a set', {
            price: formatCurrency(bundle?.totalDiscount, {
              currency,
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }),
          })}
        </div>
      )}
      <div className="flex flex-col items-center px-8 text-center">
        <h3 className="text-2xl font-bold md:mb-4">
          {optionalTitle ||
            productVariant?.product?.name ||
            bundle?.name ||
            t('Smart Hosting Business')}
        </h3>
        {!isHidden && (
          <>
            <RichTextRenderer className="mb-12" blocks={shortDescription} />
            {/* eslint-disable */}
            {bundle?.recurringSubscriptionPrice ? (
              <p className="yellow-underline text-3xl font-bold">
                {formatCurrency(bundle.recurringSubscriptionPrice, {
                  currency,
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </p>
            ) : subscriptionRecurringPrice ? (
              <p className="yellow-underline text-3xl font-bold">
                {formatCurrency(subscriptionRecurringPrice, {
                  currency,
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </p>
            ) : (
              <p className=" text-3xl font-bold">
                <span className="yellow-underline">{t('Tailored plan')}</span>
              </p>
            )}
            <p className=" mb-3 text-neutral-50">
              {/* Differentiate between normal plan and tailored plan*/}
              {bundle?.hasSubscription || subscriptionRecurringPrice
                ? t('per Smart Lock/year')
                : t('from 5+ properties')}
            </p>
            <div className="mb-8 md:min-h-[160px]">
              {(bundle?.hasFreeTrial || subscriptionHasFreeTrial) && (
                <p className="mx-auto w-fit rounded-3xl bg-green px-2 py-1 font-bold text-white">
                  {t('30 days free trial')}
                </p>
              )}
              {bundle && (
                <>
                  <p className="my-2 font-bold">+</p>
                  <p>
                    {bundle.totalDiscount > 0 && (
                      <span className=" text-neutral-50 line-through">
                        {formatCurrency(smartlockFromBundle?.priceVariant, {
                          currency,
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}{' '}
                      </span>
                    )}
                    <span className="font-bold">
                      {formatCurrency(
                        (smartlockFromBundle?.priceVariant?.price ?? 0) - bundle.totalDiscount,
                        {
                          currency,
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        },
                      )}
                    </span>
                    <span className="font-bold"> {t('for the')} </span>
                    <span className="font-bold">{bundle.firstProductName}</span>
                    <span className="block text-neutral-50">({t('one-time payment')})</span>
                  </p>
                </>
              )}
            </div>
            <Button
              className={twJoin(
                'mb-10 uppercase',
                !isMiddleCard && 'bg-white hover:bg-primary-100',
              )}
              href={buttonLink || optionalButtonLink || '#'}
              data-tile-element-id={ctaButtonTrackingId}
            >
              {buttonText}
            </Button>
          </>
        )}
      </div>
      {!isHidden && (
        <>
          <RichTextRenderer blocks={featuresHeading} className="mb-4" />
          <RichTextRenderer blocks={features} className="custom-list text-left" />
        </>
      )}
      {!isMiddleCard && isHidden && (
        <button
          onClick={() => setIsHidden(false)}
          className="mb-1 block w-full"
          aria-label="Open Card"
          type="button"
        >
          <ChevronDownIcon className="mx-auto w-12" />
        </button>
      )}
    </div>
  );
}
